// [Phase 6a foundation] Feature flag provider + hook.
//
// Adapted from Cal.com's `packages/features/flags/context/provider.ts`
// + `packages/features/flags/hooks/useIsFeatureEnabledForTeam.ts`. Two
// changes for our setup:
//   1. Tenant scoping is by `studioId` (string) not `teamId` (number).
//   2. Flat string-literal `AppFlags` type — no Cal.com-style nested
//      records. All flags are global by default; per-tenant overrides
//      live in `flagsByStudio[studioId]`.
//
// Flag values are server-driven: the AuthProvider's session JSON
// includes a `flags` slot (currently absent on the MockAuthAdapter —
// added when the FirebaseAuthAdapter is wired). For now consumers
// pass `value` explicitly; tests and storybook stub it.
import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

// ────────────────────────────────────────────────────────────────────────
// AppFlags — the canonical list. Every flag is opt-in: missing = false.
// ────────────────────────────────────────────────────────────────────────

export type AppFlags = {
  /** Phase 2 MCP tools (guild_compliance_check etc) when enabled per-tenant. */
  "phase2-tools": boolean;
  /** Reveal the experimental L2 Insights section on Home. */
  "experimental-insights": boolean;
  /** Show the index-health dashboard sidebar item. */
  "index-health": boolean;
  /** Allow the active-deal pill to be edited from Home. */
  "active-deal-edit": boolean;
};

export type FlagName = keyof AppFlags;

export type FlagSnapshot = {
  /** Global flags (apply to every tenant unless overridden). */
  global: Partial<AppFlags>;
  /** Per-studio overrides — exact match on studioId wins. */
  flagsByStudio?: Record<string, Partial<AppFlags>>;
};

const FeatureContext = createContext<FlagSnapshot | null>(null);

export function FeatureProvider({
  value,
  children,
}: {
  value: FlagSnapshot;
  children: ReactNode;
}): JSX.Element {
  // Memo guards against re-creating the context on every parent render.
  const stable = useMemo<FlagSnapshot>(
    () => ({ global: value.global, flagsByStudio: value.flagsByStudio }),
    [value.global, value.flagsByStudio]
  );
  return (
    <FeatureContext.Provider value={stable}>{children}</FeatureContext.Provider>
  );
}

/**
 * Returns the boolean value of a flag, with per-studio override taking
 * precedence over the global value. Missing flags are `false`.
 */
export function useFeatureFlag(
  name: FlagName,
  studioId: string | null = null
): boolean {
  const ctx = useContext(FeatureContext);
  if (ctx === null) {
    throw new Error(
      "useFeatureFlag must be called inside <FeatureProvider>"
    );
  }
  if (
    studioId !== null &&
    ctx.flagsByStudio !== undefined &&
    ctx.flagsByStudio[studioId] !== undefined &&
    ctx.flagsByStudio[studioId][name] !== undefined
  ) {
    return ctx.flagsByStudio[studioId][name] === true;
  }
  return ctx.global[name] === true;
}

export function useFeatureFlags(): FlagSnapshot {
  const ctx = useContext(FeatureContext);
  if (ctx === null) {
    throw new Error(
      "useFeatureFlags must be called inside <FeatureProvider>"
    );
  }
  return ctx;
}

/**
 * Convenience render-prop variant for templates that want to gate
 * a subtree without dropping into a hook.
 */
export function FeatureGate({
  name,
  studioId,
  children,
  fallback,
}: {
  name: FlagName;
  studioId?: string | null;
  children: ReactNode;
  fallback?: ReactNode;
}): JSX.Element {
  const enabled = useFeatureFlag(name, studioId ?? null);
  return <>{enabled ? children : (fallback ?? null)}</>;
}

/** Used by tests + storybook. */
export const EMPTY_FLAGS: FlagSnapshot = { global: {} };
